/* global customElements, HTMLElement */

if (!customElements.get('video-banner-gallery')) {
  class VideoBannerGallery extends HTMLElement {
    constructor() {
      super();
      this.slides = [];
      this.thumbs = [];
      this.arrowsAndDirCtrls = [];
      this.playPauseBtn = null;
      this.titleEl = null;
      this.currentIdxEl = null;
      this.timerBarEl = null;
      this.timerElapsedEl = null;
      this.timerRemainingEl = null;
      this.current = 0;
      this.timerRunning = false;
      this.timerFrame = null;
      this.lastTickTs = null;
      this.elapsedMs = 0;
      this.io = null;
      this.userPaused = false;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.video-banner__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.video-banner__thumb'));
      this.arrowsAndDirCtrls = Array.from(
        this.querySelectorAll('[data-direction]')
      );
      this.playPauseBtn = this.querySelector('[data-action="playpause"]');
      this.titleEl = this.querySelector('[data-widget-title]');
      this.currentIdxEl = this.querySelector('[data-widget-current]');
      this.timerBarEl = this.querySelector('[data-widget-timer-bar]');
      this.timerElapsedEl = this.querySelector('[data-widget-elapsed]');
      this.timerRemainingEl = this.querySelector('[data-widget-remaining]');

      if (this.slides.length === 0) return;

      this.autoscroll =
        this.dataset.autoscroll === 'true' && this.slides.length > 1;
      this.speed =
        Math.max(2, parseInt(this.dataset.autoscrollSpeed, 10) || 6) * 1000;

      this.autoplay = this.dataset.autoplay !== 'false';

      // Bind handlers
      this._onThumbClick = this._onThumbClick.bind(this);
      this._onDirectionClick = this._onDirectionClick.bind(this);
      this._onPlayPauseClick = this._onPlayPauseClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', this._onThumbClick);
        thumb.dataset.slideIndex = String(index);
      });

      this.arrowsAndDirCtrls.forEach((btn) => {
        btn.addEventListener('click', this._onDirectionClick);
      });

      if (this.playPauseBtn) {
        this.playPauseBtn.addEventListener('click', this._onPlayPauseClick);
      }

      this.addEventListener('keydown', this._onKeydown);



      document.addEventListener('visibilitychange', this._onVisibilityChange);

      // Update widget UI for initial slide (title/counter).
      this.showSlide(0, { restartTimer: false });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this.userPaused) {
                  this._playActiveMedia();
                  this.startTimer();
                }
              } else {
                this._pauseAllMedia();
                this.stopTimer();
              }
            });
          },
          { threshold: 0.15 }
        );
        this.io.observe(this);
      } else if (!this.userPaused) {
        this._playActiveMedia();
        this.startTimer();
      }
    }

    disconnectedCallback() {
      this.stopTimer();
      this.thumbs.forEach((thumb) =>
        thumb.removeEventListener('click', this._onThumbClick)
      );
      this.arrowsAndDirCtrls.forEach((btn) =>
        btn.removeEventListener('click', this._onDirectionClick)
      );
      if (this.playPauseBtn) {
        this.playPauseBtn.removeEventListener('click', this._onPlayPauseClick);
      }
      this.removeEventListener('keydown', this._onKeydown);

      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      if (this.io) this.io.disconnect();
      this._pauseAllMedia();
    }

    _onThumbClick(event) {
      const target = event.currentTarget;
      const index = parseInt(target.dataset.slideIndex, 10);
      if (Number.isNaN(index)) return;
      this.showSlide(index, { restartTimer: true });
    }

    _onDirectionClick(event) {
      const direction = event.currentTarget.dataset.direction;
      if (direction === 'prev') this.prev();
      else this.next();
      this.restartTimer();
    }

    _onPlayPauseClick() {
      if (this.userPaused) {
        this.play();
      } else {
        this.pause();
      }
    }

    _onKeydown(event) {
      if (
        event.target.classList &&
        event.target.classList.contains('video-banner__thumb')
      ) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.prev();
        }
      }
    }



    _onVisibilityChange() {
      if (document.hidden) {
        this._pauseAllMedia();
        this.stopTimer();
      } else if (!this.userPaused) {
        this._playActiveMedia();
        this.startTimer();
      }
    }

    showSlide(index, options = {}) {
      const { restartTimer = false } = options;
      if (index < 0 || index >= this.slides.length) return;

      this.current = index;

      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.elapsedMs = 0;
      this._renderTimerMeta();

      // Update widget meta
      this._updateWidgetMeta(index);

      // Update media playback
      this.slides.forEach((slide, i) => {
        if (i === index) {
          if (!this.userPaused) this._playSlideMedia(slide);
        } else {
          this._pauseSlideMedia(slide);
        }
      });

      this.dispatchEvent(
        new CustomEvent('video-banner:change', {
          detail: { index },
          bubbles: true,
        })
      );

      if (restartTimer) this.restartTimer();
    }

    _updateWidgetMeta(index) {
      // Update title from active thumb's data-slide-title
      if (this.titleEl && this.thumbs[index]) {
        const title = this.thumbs[index].dataset.slideTitle || '';
        this.titleEl.textContent = title;
      }
      // Update current index (1-based, zero-padded to 3 digits)
      if (this.currentIdxEl) {
        const num = String(index + 1).padStart(3, '0');
        this.currentIdxEl.textContent = num;
      }
    }

    _playSlideMedia(slide) {
      if (!this.autoplay || this.userPaused) return;

      const video = slide.querySelector('video');
      if (video) {
        video.muted = video.hasAttribute('data-muted')
          ? video.dataset.muted !== 'false'
          : true;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            /* autoplay blocked; user can interact to resume */
          });
        }
      }

      const external = slide.querySelector('.video-banner__external');
      if (external && !external.querySelector('iframe')) {
        const iframe = this._buildExternalIframe(external);
        if (iframe) external.appendChild(iframe);
      }
    }

    _pauseSlideMedia(slide) {
      const video = slide.querySelector('video');
      if (video && typeof video.pause === 'function') {
        video.pause();
      }
      const external = slide.querySelector('.video-banner__external');
      if (external) {
        const iframe = external.querySelector('iframe');
        if (iframe) iframe.remove();
      }
    }

    _pauseAllMedia() {
      this.slides.forEach((slide) => this._pauseSlideMedia(slide));
    }

    _playActiveMedia() {
      const slide = this.slides[this.current];
      if (slide) this._playSlideMedia(slide);
    }

    _buildExternalIframe(container) {
      const type = container.dataset.videoUrlType;
      const id = container.dataset.videoUrlId;
      const title = container.dataset.videoTitle || 'Video';
      if (!type || !id) return null;

      const iframe = document.createElement('iframe');
      let src = '';

      if (type === 'youtube') {
        const params = new URLSearchParams({
          autoplay: '1',
          mute: '1',
          loop: '1',
          playlist: id,
          controls: '0',
          playsinline: '1',
          modestbranding: '1',
          rel: '0',
          disablekb: '1',
          iv_load_policy: '3',
        });
        src = `https://www.youtube.com/embed/${id}?${params.toString()}`;
      } else if (type === 'vimeo') {
        const params = new URLSearchParams({
          autoplay: '1',
          muted: '1',
          loop: '1',
          controls: '0',
          background: '1',
          playsinline: '1',
          dnt: '1',
        });
        src = `https://player.vimeo.com/video/${id}?${params.toString()}`;
      } else {
        return null;
      }

      iframe.src = src;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('playsinline', '');
      iframe.setAttribute('title', title);
      iframe.setAttribute('loading', 'lazy');
      iframe.tabIndex = -1;
      return iframe;
    }

    // Public playback controls -------------------------------------------------
    play() {
      this.userPaused = false;
      this.classList.remove('is-paused');
      if (this.playPauseBtn) {
        this.playPauseBtn.setAttribute('aria-pressed', 'false');
        this.playPauseBtn.setAttribute('aria-label', 'Pause');
      }
      this._playActiveMedia();
      this.startTimer();
    }

    pause() {
      this.userPaused = true;
      this.classList.add('is-paused');
      if (this.playPauseBtn) {
        this.playPauseBtn.setAttribute('aria-pressed', 'true');
        this.playPauseBtn.setAttribute('aria-label', 'Play');
      }
      this._pauseAllMedia();
      this.stopTimer();
    }

    startTimer() {
      if (!this.autoscroll || this.timerRunning || this.userPaused) return;
      if (this.elapsedMs >= this.speed) this.elapsedMs = 0;

      this.timerRunning = true;
      this.lastTickTs = null;
      this._startTimerUiLoop();
    }

    stopTimer() {
      this.timerRunning = false;
      this.lastTickTs = null;
      this._stopTimerUiLoop();
      this._renderTimerMeta();
    }

    restartTimer() {
      this.stopTimer();
      this.elapsedMs = 0;
      this._renderTimerMeta();
      this.startTimer();
    }

    _startTimerUiLoop() {
      this._stopTimerUiLoop();

      const tick = (now) => {
        if (!this.timerRunning || this.userPaused) return;

        if (this.lastTickTs === null) {
          this.lastTickTs = now;
        }

        const delta = now - this.lastTickTs;
        this.lastTickTs = now;
        this.elapsedMs = Math.max(0, this.elapsedMs + delta);

        if (this.elapsedMs >= this.speed) {
          this.elapsedMs = 0;
          const next = (this.current + 1) % this.slides.length;
          this.showSlide(next);
        }

        this._renderTimerMeta();

        if (this.timerRunning && !this.userPaused) {
          this.timerFrame = window.requestAnimationFrame(tick);
        }
      };

      this.timerFrame = window.requestAnimationFrame(tick);
    }

    _stopTimerUiLoop() {
      if (this.timerFrame !== null) {
        window.cancelAnimationFrame(this.timerFrame);
        this.timerFrame = null;
      }
    }

    _formatClock(ms) {
      const seconds = Math.max(0, Math.floor(ms / 1000));
      const minutesPart = String(Math.floor(seconds / 60));
      const secondsPart = String(seconds % 60).padStart(2, '0');
      return `${minutesPart}:${secondsPart}`;
    }

    _renderTimerMeta() {
      const totalTime = this._formatClock(this.speed || 0);

      if (!this.autoscroll) {
        if (this.timerBarEl) this.timerBarEl.style.width = '0%';
        if (this.timerElapsedEl) this.timerElapsedEl.textContent = '0:00';
        if (this.timerRemainingEl) this.timerRemainingEl.textContent = totalTime;
        return;
      }

      const elapsed = Math.min(this.speed, Math.max(0, this.elapsedMs));
      const percent = this.speed > 0 ? (elapsed / this.speed) * 100 : 0;

      if (this.timerBarEl) {
        this.timerBarEl.style.width = `${percent}%`;
      }
      if (this.timerElapsedEl) {
        this.timerElapsedEl.textContent = this._formatClock(elapsed);
      }
      if (this.timerRemainingEl) {
        this.timerRemainingEl.textContent = totalTime;
      }
    }

    next() {
      const next = (this.current + 1) % this.slides.length;
      this.showSlide(next);
    }

    prev() {
      const prev =
        (this.current - 1 + this.slides.length) % this.slides.length;
      this.showSlide(prev);
    }
  }

  customElements.define('video-banner-gallery', VideoBannerGallery);
}
